import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import {
  ContentCopy, CheckOutlined,
  DarkModeOutlined, LightModeOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import LogoIcon from '../components/LogoIcon';

const BASE_URL = 'https://agenticqe.ai/v1';

// ─── Code block ──────────────────────────────────────────────────────────────
const CodeBlock: React.FC<{ code: string; label?: string }> = ({ code, label }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(code.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Box sx={{ position: 'relative', my: 2.5 }}>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Chip
            label={label}
            size="small"
            sx={{
              fontSize: '0.65rem', height: 20, fontWeight: 600,
              bgcolor: isDark ? '#1A1A1A' : '#EBEBEB',
              color: isDark ? '#999' : '#555',
              border: 'none', letterSpacing: '0.03em',
            }}
          />
        </Box>
      )}
      <Box sx={{ position: 'relative' }}>
        <Box
          component="pre"
          sx={{
            m: 0, p: '18px 48px 18px 18px',
            bgcolor: isDark ? '#111111' : '#F4F4F5',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
            borderRadius: '10px',
            overflowX: 'auto',
            fontSize: '0.8rem',
            lineHeight: 1.7,
            fontFamily: '"JetBrains Mono","Cascadia Code","Fira Code","Courier New",monospace',
            color: isDark ? '#D4D4D4' : '#1E1E1E',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? '#333' : '#CCC', borderRadius: 2 },
          }}
        >
          <code style={{ color: 'inherit', background: 'none' }}>{code.trim()}</code>
        </Box>
        <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
          <IconButton
            size="small"
            onClick={copy}
            sx={{
              position: 'absolute', top: 10, right: 10,
              color: isDark ? '#444' : '#BBB',
              '&:hover': { color: isDark ? '#CCC' : '#333', bgcolor: 'transparent' },
              p: 0.5,
            }}
          >
            {copied
              ? <CheckOutlined sx={{ fontSize: 14, color: '#22C55E' }} />
              : <ContentCopy sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ─── Section heading ─────────────────────────────────────────────────────────
const SectionTitle: React.FC<{ id: string; children: React.ReactNode; sub?: boolean }> = ({ id, children, sub }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  return (
    <Typography
      id={id}
      sx={{
        fontWeight: sub ? 600 : 700,
        fontSize: sub ? '1.05rem' : '1.35rem',
        color: isDark ? '#EFEFEF' : '#0A0A0A',
        letterSpacing: '-0.025em',
        mt: sub ? 4 : 6,
        mb: 1.5,
        scrollMarginTop: '72px',
      }}
    >
      {children}
    </Typography>
  );
};

// ─── Inline code ─────────────────────────────────────────────────────────────
const IC: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  return (
    <Box
      component="code"
      sx={{
        fontFamily: '"JetBrains Mono","Courier New",monospace',
        fontSize: '0.82em',
        px: 0.75, py: 0.2,
        bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)',
        borderRadius: '4px',
        color: isDark ? '#E0E0E0' : '#1A1A1A',
      }}
    >
      {children}
    </Box>
  );
};

// ─── Method badge ─────────────────────────────────────────────────────────────
const MethodBadge: React.FC<{ method: string }> = ({ method }) => {
  const colors: Record<string, string> = {
    GET: '#22C55E', POST: '#3B82F6', DELETE: '#EF4444', PATCH: '#F59E0B', PUT: '#8B5CF6',
  };
  const c = colors[method] ?? '#888';
  return (
    <Chip
      label={method}
      size="small"
      sx={{
        bgcolor: `${c}20`, color: c, fontWeight: 700,
        fontSize: '0.7rem', height: 22, border: `1px solid ${c}40`,
        fontFamily: 'monospace',
      }}
    />
  );
};

// ─── Info / warning box ───────────────────────────────────────────────────────
const InfoBox: React.FC<{ title: string; color: string; children: React.ReactNode }> = ({ title, color, children }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  return (
    <Box sx={{
      p: 2.5, my: 2,
      bgcolor: isDark ? `${color}12` : `${color}10`,
      border: `1px solid ${color}35`,
      borderLeft: `3px solid ${color}`,
      borderRadius: '8px',
    }}>
      <Typography sx={{ fontSize: '0.82rem', color, fontWeight: 700, mb: 0.75 }}>{title}</Typography>
      <Box sx={{ fontSize: '0.82rem', color: isDark ? '#AAAAAA' : '#444444', lineHeight: 1.65 }}>
        {children}
      </Box>
    </Box>
  );
};

// ─── Error codes ──────────────────────────────────────────────────────────────
const ERROR_CODES = [
  { code: '400', label: 'Bad Request',          cause: 'Model not allowed for this endpoint, or malformed request body.' },
  { code: '401', label: 'Unauthorized',          cause: 'Missing or invalid Authorization Bearer token — OR the stored OpenAI API key for this app has been detected as invalid. Check the error detail for a [ProxyWrapper] prefix to distinguish the two.' },
  { code: '403', label: 'Forbidden',             cause: 'App not permitted for this endpoint, or app is inactive.' },
  { code: '404', label: 'Not Found',             cause: 'Endpoint path not recognised by the proxy.' },
  { code: '422', label: 'Unprocessable Entity',  cause: 'Request body failed schema validation.' },
  { code: '429', label: 'Token Limit Exceeded',  cause: 'Daily input or output token quota exhausted.' },
  { code: '500', label: 'Internal Server Error', cause: 'Unexpected proxy-side error — retry with backoff.' },
  { code: '503', label: 'Service Unavailable',   cause: 'Cannot reach the upstream OpenAI API.' },
];

// ─── Sidebar nav ──────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'overview',        label: 'Overview' },
  { id: 'authentication',  label: 'Authentication' },
  { id: 'quickstart',      label: 'Quick Start' },
  { id: 'endpoints',       label: 'API Reference', children: [
    { id: 'ep-models',     label: 'GET /v1/models' },
    { id: 'ep-chat',       label: 'POST /v1/chat/completions' },
    { id: 'ep-embeddings', label: 'POST /v1/embeddings' },
    { id: 'ep-audio',      label: 'POST /v1/audio/transcriptions' },
    { id: 'ep-images',     label: 'POST /v1/images/generations' },
  ]},
  { id: 'errors',          label: 'Error Handling', children: [
    { id: 'err-prefixes',  label: 'Error Prefixes' },
    { id: 'err-codes',     label: 'Error Codes' },
  ]},
  { id: 'limits',          label: 'Token Limits' },
  { id: 'examples',        label: 'SDK Examples', children: [
    { id: 'ex-curl',       label: 'cURL' },
    { id: 'ex-python',     label: 'Python' },
    { id: 'ex-node',       label: 'Node.js / TypeScript' },
    { id: 'ex-openai-sdk', label: 'OpenAI SDK (drop-in)' },
  ]},
  { id: 'frameworks',      label: 'Agent Frameworks', children: [
    { id: 'fw-langchain',  label: 'LangChain' },
    { id: 'fw-llamaindex', label: 'LlamaIndex' },
    { id: 'fw-crewai',     label: 'CrewAI' },
    { id: 'fw-autogen',    label: 'AutoGen / AG2' },
  ]},
  { id: 'best-practices',  label: 'Best Practices' },
];

// ─── Main component ───────────────────────────────────────────────────────────
const DocsPage: React.FC = () => {
  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeMode();
  const isDark = mode === 'dark';
  const [activeId, setActiveId] = useState('overview');
  const contentRef = useRef<HTMLDivElement>(null);

  const bg     = isDark ? '#0A0A0A' : '#FFFFFF';
  const bgPage = isDark ? '#0A0A0A' : '#FAFAFA';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)';
  const textP  = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS  = isDark ? '#999999' : '#555555';
  const textM  = isDark ? '#555555' : '#AAAAAA';

  useEffect(() => {
    const allIds = NAV_ITEMS.flatMap(n => [n.id, ...(n.children?.map(c => c.id) ?? [])]);
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          const id = visible[0].target.id;
          setActiveId(id);
          window.history.replaceState(null, '', `/docs#${id}`);
        }
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 },
    );
    allIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `/docs#${id}`);
  };

  // On direct load with hash — scroll to section
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
  }, []);

  const para = (text: string) => (
    <Typography sx={{ fontSize: '0.925rem', color: textS, lineHeight: 1.8, mb: 1.75 }}>
      {text}
    </Typography>
  );

  const divider = () => (
    <Box sx={{ height: '1px', bgcolor: border, my: 4 }} />
  );

  return (
    <Box className="page-transition" sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: bgPage }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <Box sx={{
        height: 58, position: 'sticky', top: 0, zIndex: 100,
        bgcolor: bg, borderBottom: `1px solid ${border}`,
        display: 'flex', alignItems: 'center', px: 4, gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/home')}>
          <LogoIcon size={18} letterColor={isDark ? '#EFEFEF' : '#FF1A50'} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: textP, letterSpacing: '-0.02em' }}>
            LLM Wrapper
          </Typography>
        </Box>
        <Box sx={{ width: '1px', height: 16, bgcolor: border }} />
        <Chip
          label="API Docs"
          size="small"
          sx={{ bgcolor: isDark ? 'rgba(255,26,80,0.12)' : '#FFF0F3', color: '#FF1A50', fontWeight: 600, fontSize: '0.68rem', height: 20, border: 'none' }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title={isDark ? 'Light mode' : 'Dark mode'} arrow>
          <IconButton size="small" onClick={toggleTheme} sx={{ color: textM, '&:hover': { color: textP } }}>
            {isDark ? <LightModeOutlined sx={{ fontSize: 16 }} /> : <DarkModeOutlined sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <Box
          onClick={() => navigate('/login')}
          sx={{
            px: 2, py: 0.75, borderRadius: '7px', cursor: 'pointer',
            border: `1px solid ${border}`, fontSize: '0.8rem', fontWeight: 500,
            color: textS, display: 'flex', alignItems: 'center', gap: 0.75,
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' },
            transition: 'background 0.1s',
          }}
        >
          Sign In <OpenInNewOutlined sx={{ fontSize: 13 }} />
        </Box>
      </Box>

      {/* ── Body ───────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, width: '100%', px: 3 }}>

        {/* Sidebar */}
        <Box sx={{
          width: 230, flexShrink: 0, position: 'sticky', top: 58,
          height: 'calc(100vh - 58px)', overflowY: 'auto', py: 5, pr: 4,
          '&::-webkit-scrollbar': { width: 0 },
        }}>
          {NAV_ITEMS.map(item => (
            <Box key={item.id} sx={{ mb: 0.25 }}>
              <Box
                onClick={() => scrollTo(item.id)}
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: activeId === item.id ? 600 : 500,
                  color: activeId === item.id ? '#FF1A50' : textS,
                  cursor: 'pointer', py: 0.6,
                  transition: 'color 0.15s',
                  '&:hover': { color: textP },
                }}
              >
                {item.label}
              </Box>
              {item.children?.map(child => (
                <Box
                  key={child.id}
                  onClick={() => scrollTo(child.id)}
                  sx={{
                    fontSize: child.id.startsWith('ep-') ? '0.72rem' : '0.76rem',
                    fontFamily: child.id.startsWith('ep-') ? '"JetBrains Mono",monospace' : 'inherit',
                    fontWeight: activeId === child.id ? 600 : 400,
                    color: activeId === child.id ? '#FF1A50' : textM,
                    cursor: 'pointer', pl: 1.5, py: 0.35,
                    transition: 'color 0.15s',
                    '&:hover': { color: textS },
                  }}
                >
                  {child.label}
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        {/* Content */}
        <Box
          ref={contentRef}
          sx={{
            flex: 1, py: 5, pl: 5, pr: 2,
            borderLeft: `1px solid ${border}`,
            minWidth: 0,
          }}
        >

          {/* ── Overview ─────────────────────────────────────────── */}
          <SectionTitle id="overview">Overview</SectionTitle>
          {para('LLM Wrapper is an OpenAI-compatible reverse proxy that sits between your application and the OpenAI API. It adds authentication, daily token budgeting, per-app rate limiting, endpoint-level permissions, and full audit logging — with zero changes to your existing OpenAI code.')}
          {para(`Point your code at ${BASE_URL} and use your app's auth key as the API key. Every request/response shape is identical to the OpenAI API.`)}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', my: 2.5 }}>
            {[
              ['Auth Key per App', '#3B82F6'],
              ['Daily Token Limits', '#22C55E'],
              ['Endpoint Permissions', '#8B5CF6'],
              ['OpenAI-Compatible', '#F59E0B'],
              ['Streaming Support', '#EC4899'],
              ['Full Audit Logs', '#14B8A6'],
            ].map(([label, color]) => (
              <Chip
                key={label} label={label} size="small"
                sx={{ bgcolor: `${color}18`, color, fontWeight: 600, fontSize: '0.73rem', border: `1px solid ${color}30` }}
              />
            ))}
          </Box>

          {divider()}

          {/* ── Authentication ───────────────────────────────────── */}
          <SectionTitle id="authentication">Authentication</SectionTitle>
          {para('Every request must include your app\'s auth key as a Bearer token. The key is generated when an admin registers your app in the dashboard.')}
          <CodeBlock label="HTTP Header" code={`Authorization: Bearer YOUR_AUTH_KEY`} />
          <InfoBox title="Recommended: use standard OpenAI env vars" color="#3B82F6">
            <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7, color: 'inherit' }}>
              Since this wrapper is a drop-in replacement for OpenAI, you can store your auth key in{' '}
              <IC>OPENAI_API_KEY</IC> and the base URL in <IC>OPENAI_BASE_URL</IC>. The OpenAI SDK and most
              agent frameworks pick these up automatically — no code changes required.
            </Typography>
            <CodeBlock code={`
export OPENAI_API_KEY=your_auth_key_here\nexport OPENAI_BASE_URL=https://agenticqe.ai/v1`} />
          </InfoBox>
          <InfoBox title="Security note" color="#F59E0B">
            <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7, color: 'inherit' }}>
              Store your key in an environment variable — never hard-code it in source files or commit it to version control. Keys are shown only once at registration; admins can re-generate them from the dashboard.
            </Typography>
          </InfoBox>

          {divider()}

          {/* ── Quick Start ──────────────────────────────────────── */}
          <SectionTitle id="quickstart">Quick Start</SectionTitle>
          {para('Two environment variables are all you need. Everything else stays the same as standard OpenAI usage.')}
          <CodeBlock label="Shell — set env vars once" code={`
export OPENAI_API_KEY=your_auth_key_here\nexport OPENAI_BASE_URL=https://agenticqe.ai/v1`} />
          <CodeBlock label="cURL — send your first request" code={`curl -X POST https://agenticqe.ai/v1/chat/completions \\
  -H "Authorization: Bearer $OPENAI_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`} />

          {divider()}

          {/* ── Endpoints ────────────────────────────────────────── */}
          <SectionTitle id="endpoints">API Reference</SectionTitle>
          {para('All endpoints mirror the OpenAI API specification. The proxy validates auth and permissions, then forwards the request transparently to OpenAI.')}

          {/* Models */}
          <SectionTitle id="ep-models" sub>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MethodBadge method="GET" />
              <IC>/v1/models</IC>
            </Box>
          </SectionTitle>
          {para('Returns the filtered list of models your application is permitted to use on this endpoint.')}
          <CodeBlock label="cURL" code={`curl https://agenticqe.ai/v1/models \\
  -H "Authorization: Bearer $OPENAI_API_KEY"`} />
          <CodeBlock label="Response" code={`{
  "object": "list",
  "data": [
    { "id": "gpt-4o",      "object": "model", "owned_by": "openai" },
    { "id": "gpt-4o-mini", "object": "model", "owned_by": "openai" }
  ]
}`} />

          {/* Chat */}
          <SectionTitle id="ep-chat" sub>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MethodBadge method="POST" />
              <IC>/v1/chat/completions</IC>
            </Box>
          </SectionTitle>
          {para('Create a chat completion. Supports both streaming (server-sent events) and non-streaming responses.')}
          <CodeBlock label="cURL — non-streaming" code={`curl -X POST https://agenticqe.ai/v1/chat/completions \\
  -H "Authorization: Bearer $OPENAI_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o",
    "messages": [
      {"role": "system", "content": "You are a helpful assistant."},
      {"role": "user",   "content": "What is 2 + 2?"}
    ],
    "temperature": 0.7,
    "max_tokens": 256
  }'`} />
          <CodeBlock label="cURL — streaming" code={`curl -X POST https://agenticqe.ai/v1/chat/completions \\
  -H "Authorization: Bearer $OPENAI_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"gpt-4o","messages":[{"role":"user","content":"Count 1–5"}],"stream":true}'`} />

          {/* Embeddings */}
          <SectionTitle id="ep-embeddings" sub>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MethodBadge method="POST" />
              <IC>/v1/embeddings</IC>
            </Box>
          </SectionTitle>
          {para('Generate vector embeddings for one or more text inputs.')}
          <CodeBlock label="cURL" code={`curl -X POST https://agenticqe.ai/v1/embeddings \\
  -H "Authorization: Bearer $OPENAI_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"text-embedding-3-small","input":"The quick brown fox"}'`} />

          {/* Audio */}
          <SectionTitle id="ep-audio" sub>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MethodBadge method="POST" />
              <IC>/v1/audio/transcriptions</IC>
            </Box>
          </SectionTitle>
          {para('Transcribe audio files using Whisper. Send as multipart/form-data.')}
          <CodeBlock label="cURL" code={`curl -X POST https://agenticqe.ai/v1/audio/transcriptions \\
  -H "Authorization: Bearer $OPENAI_API_KEY" \\
  -F "file=@audio.mp3" \\
  -F "model=whisper-1"`} />

          {/* Images */}
          <SectionTitle id="ep-images" sub>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MethodBadge method="POST" />
              <IC>/v1/images/generations</IC>
            </Box>
          </SectionTitle>
          {para('Generate images from a text prompt using DALL·E.')}
          <CodeBlock label="cURL" code={`curl -X POST https://agenticqe.ai/v1/images/generations \\
  -H "Authorization: Bearer $OPENAI_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "dall-e-3",
    "prompt": "A serene mountain landscape at sunset",
    "n": 1,
    "size": "1024x1024"
  }'`} />

          {divider()}

          {/* ── Error Handling ───────────────────────────────────── */}
          <SectionTitle id="errors">Error Handling</SectionTitle>

          <SectionTitle id="err-prefixes" sub>Error Prefixes</SectionTitle>
          {para('Every error response includes a prefix in the message field that tells you exactly where the error originated. This makes debugging and log filtering straightforward.')}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, my: 2.5 }}>
            {/* Wrapper error */}
            <Box sx={{
              p: 2.5, border: `1px solid rgba(239,68,68,0.25)`, borderLeft: '3px solid #EF4444',
              borderRadius: '10px', bgcolor: isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)',
            }}>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: '#EF4444', mb: 1 }}>
                [ProxyWrapper]
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: textS, lineHeight: 1.65 }}>
                Error originated inside the LLM Wrapper. Caused by auth failures, permission checks, token limit exceeded, or proxy-level issues.
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: textM, mt: 1.5, fontStyle: 'italic' }}>
                Fix: Check your auth key, app permissions, or daily quota.
              </Typography>
            </Box>

            {/* OpenAI error */}
            <Box sx={{
              p: 2.5, border: `1px solid rgba(245,158,11,0.25)`, borderLeft: '3px solid #F59E0B',
              borderRadius: '10px', bgcolor: isDark ? 'rgba(245,158,11,0.06)' : 'rgba(245,158,11,0.04)',
            }}>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: '#F59E0B', mb: 1 }}>
                [Error from OpenAI]
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: textS, lineHeight: 1.65 }}>
                Error forwarded directly from the OpenAI API. The wrapper received an error response from upstream and passed it back to you unchanged.
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: textM, mt: 1.5, fontStyle: 'italic' }}>
                Fix: Check the OpenAI status page or adjust your request parameters.
              </Typography>
            </Box>
          </Box>

          <CodeBlock label="[ProxyWrapper] example — token limit exceeded" code={`{
  "detail": "[ProxyWrapper] Output token limit for model gpt-4o exceeded for today"
}`} />
          <CodeBlock label="[ProxyWrapper] example — invalid OpenAI API key (HTTP 401)" code={`{
  "detail": "[ProxyWrapper] The OpenAI API key for 'My App' is invalid. Ask your admin to update it at https://dashboard.agenticqe.ai"
}`} />
          <CodeBlock label="[Error from OpenAI] example — invalid request" code={`{
  "error": {
    "message": "[Error from OpenAI] This model's maximum context length is 128000 tokens.",
    "type": "invalid_request_error",
    "code": "context_length_exceeded"
  }
}`} />

          <InfoBox title="Automatic Invalid Key Detection" color="#F59E0B">
            <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7 }}>
              The proxy monitors OpenAI responses in real time. If OpenAI rejects the stored API key with a 401,
              the app is immediately flagged as <IC>api_key_invalid</IC> and <strong>all future requests to that app are blocked</strong> — no further calls reach OpenAI until the key is fixed.
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7, mt: 1 }}>
              When this happens your application will receive <IC>HTTP 401</IC> with a{' '}
              <IC>[ProxyWrapper]</IC> detail message directing your admin to the dashboard to update the key.
              The proxy also runs scheduled key health checks twice daily (07:00 and 19:00 UTC) across all registered apps.
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7, mt: 1 }}>
              <strong>For admins:</strong> Open the Applications page, find the app with an <IC>Invalid Key</IC> badge,
              click the app to open its detail view, and paste a new OpenAI API key into the update form.
              Service resumes immediately after saving.
            </Typography>
          </InfoBox>

          <SectionTitle id="err-codes" sub>Error Codes</SectionTitle>
          {para('The proxy returns standard HTTP status codes alongside an error object. Handle these in your application for a robust integration.')}
          <Box sx={{ border: `1px solid ${border}`, borderRadius: '10px', overflow: 'hidden', my: 2.5 }}>
            <Box sx={{
              display: 'grid', gridTemplateColumns: '72px 1fr 2fr',
              bgcolor: isDark ? '#111' : '#F5F5F5',
              borderBottom: `1px solid ${border}`, px: 2.5, py: 1.25,
            }}>
              {['Code', 'Meaning', 'Common Cause'].map(h => (
                <Typography key={h} sx={{ fontSize: '0.7rem', fontWeight: 700, color: textM, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {h}
                </Typography>
              ))}
            </Box>
            {ERROR_CODES.map((row, i) => {
              const codeColor =
                row.code === '401' || row.code === '403' ? '#EF4444' :
                row.code === '429' ? '#F59E0B' :
                row.code === '400' || row.code === '422' ? '#F97316' : textS;
              return (
                <Box
                  key={row.code}
                  sx={{
                    display: 'grid', gridTemplateColumns: '72px 1fr 2fr',
                    px: 2.5, py: 1.5, alignItems: 'start',
                    bgcolor: i % 2 === 0 ? 'transparent' : (isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)'),
                    borderBottom: i < ERROR_CODES.length - 1 ? `1px solid ${border}` : 'none',
                  }}
                >
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.84rem', color: codeColor }}>
                    {row.code}
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: textP, fontWeight: 500 }}>{row.label}</Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: textS, lineHeight: 1.6 }}>{row.cause}</Typography>
                </Box>
              );
            })}
          </Box>

          {divider()}

          {/* ── Token Limits ─────────────────────────────────────── */}
          <SectionTitle id="limits">Token Limits</SectionTitle>
          {para('Each application has a daily token budget split into input tokens and output tokens. These reset at midnight UTC. When the budget is exhausted the proxy returns HTTP 429 until the next reset.')}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, my: 2.5 }}>
            {[
              { label: 'Input tokens', desc: 'Tokens in your messages and system prompt. Counted before the request is forwarded to OpenAI.', color: '#22D3EE' },
              { label: 'Output tokens', desc: "Tokens in the model's response. Counted from the usage field returned by OpenAI.", color: '#A855F7' },
            ].map(({ label, desc, color }) => (
              <Box key={label} sx={{ p: 2.5, border: `1px solid ${border}`, borderRadius: '10px', borderLeft: `3px solid ${color}` }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color, mb: 0.75 }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: textS, lineHeight: 1.65 }}>{desc}</Typography>
              </Box>
            ))}
          </Box>
          {para('Admins configure input and output budgets independently per application via the dashboard.')}

          {divider()}

          {/* ── SDK Examples ─────────────────────────────────────── */}
          <SectionTitle id="examples">SDK Examples</SectionTitle>
          {para('Set OPENAI_API_KEY and OPENAI_BASE_URL once and your existing OpenAI code works without any further changes.')}

          {/* cURL */}
          <SectionTitle id="ex-curl" sub>cURL</SectionTitle>
          <CodeBlock label="Chat completion" code={`curl -X POST https://agenticqe.ai/v1/chat/completions \\
  -H "Authorization: Bearer $OPENAI_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o",
    "messages": [{"role": "user", "content": "Hello!"}],
    "max_tokens": 200
  }'`} />
          <CodeBlock label="List permitted models" code={`curl https://agenticqe.ai/v1/models \\
  -H "Authorization: Bearer $OPENAI_API_KEY"`} />

          {/* Python */}
          <SectionTitle id="ex-python" sub>Python</SectionTitle>
          <CodeBlock label="Python — requests" code={`import os
import requests

# Set via: export OPENAI_API_KEY=... and OPENAI_BASE_URL=https://agenticqe.ai/v1
BASE_URL = os.environ["OPENAI_BASE_URL"]
API_KEY  = os.environ["OPENAI_API_KEY"]

headers = {
    "Authorization": f"Bearer {API_KEY}",
    "Content-Type": "application/json",
}

def chat(messages, model="gpt-4o", **kwargs):
    resp = requests.post(
        f"{BASE_URL}/chat/completions",
        headers=headers,
        json={"model": model, "messages": messages, **kwargs},
        timeout=30,
    )
    resp.raise_for_status()
    return resp.json()

result = chat([{"role": "user", "content": "Hello!"}])
print(result["choices"][0]["message"]["content"])
print("Tokens used:", result["usage"]["total_tokens"])`} />

          {/* Node.js */}
          <SectionTitle id="ex-node" sub>Node.js / TypeScript</SectionTitle>
          <CodeBlock label="Node.js — axios" code={`import axios from 'axios';

// Set via: export OPENAI_API_KEY=... and OPENAI_BASE_URL=https://agenticqe.ai/v1
const client = axios.create({
  baseURL: process.env.OPENAI_BASE_URL,
  headers: { Authorization: \`Bearer \${process.env.OPENAI_API_KEY}\` },
  timeout: 30_000,
});

async function chat(
  messages: { role: string; content: string }[],
  model = 'gpt-4o',
) {
  const { data } = await client.post('/chat/completions', { model, messages });
  return data;
}

const response = await chat([{ role: 'user', content: 'Hello!' }]);
console.log(response.choices[0].message.content);`} />

          {/* OpenAI SDK */}
          <SectionTitle id="ex-openai-sdk" sub>OpenAI SDK — drop-in replacement</SectionTitle>
          {para('The OpenAI SDK automatically reads OPENAI_API_KEY and OPENAI_BASE_URL from the environment. No code changes needed — just set the env vars.')}
          <CodeBlock label="Python — OpenAI SDK" code={`# pip install openai
import os
from openai import OpenAI

# SDK auto-reads OPENAI_API_KEY and OPENAI_BASE_URL from env
client = OpenAI()  # no arguments needed when env vars are set

# — or explicitly:
# client = OpenAI(
#     api_key=os.environ["OPENAI_API_KEY"],
#     base_url=os.environ["OPENAI_BASE_URL"],   # https://agenticqe.ai/v1
# )

response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role": "user", "content": "Hello!"}],
)
print(response.choices[0].message.content)`} />
          <CodeBlock label="TypeScript — OpenAI SDK" code={`// npm install openai
import OpenAI from 'openai';

// SDK auto-reads OPENAI_API_KEY and OPENAI_BASE_URL from env
const openai = new OpenAI();  // no arguments needed when env vars are set

// — or explicitly:
// const openai = new OpenAI({
//   apiKey: process.env.OPENAI_API_KEY,
//   baseURL: process.env.OPENAI_BASE_URL,  // https://agenticqe.ai/v1
// });

const response = await openai.chat.completions.create({
  model: 'gpt-4o',
  messages: [{ role: 'user', content: 'Hello!' }],
});
console.log(response.choices[0].message.content);`} />

          {divider()}

          {/* ── Agent Frameworks ─────────────────────────────────── */}
          <SectionTitle id="frameworks">Agent Frameworks</SectionTitle>
          {para('Because LLM Wrapper is fully OpenAI-compatible, every popular agent framework works without modification. Set the two environment variables and your existing agent code routes through the wrapper automatically.')}
          <CodeBlock label="Universal setup — set once" code={`export OPENAI_API_KEY=your_auth_key_here\nexport OPENAI_BASE_URL=https://agenticqe.ai/v1`}  />
            {para("Or, you can add these variables in your .env file as well.")}
            <CodeBlock label=".env file" code={`OPENAI_API_KEY=your_auth_key_here\nOPENAI_BASE_URL=https://agenticqe.ai/v1`} />
            <InfoBox title="How to use" color="#3B82F6">
              <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7, color: 'inherit' }}>
              Create a file named <IC>.env</IC> in your project root and paste the above lines. Most frameworks and SDKs (like Next.js, Vite, Python dotenv, etc.) will automatically load these variables.
              <br /><br />
              <b>For Node.js:</b> Use the <IC>dotenv</IC> package to load them:
              </Typography>
              <CodeBlock label="Node.js — load .env" code={`npm install dotenv

      // At the top of your entry file (e.g., index.js or app.ts)
      require('dotenv').config(); // or import 'dotenv/config' in ESM

      // Now process.env.OPENAI_API_KEY and process.env.OPENAI_BASE_URL are available
      `} />
              <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7, color: 'inherit', mt: 2 }}>
              <b>For Python:</b> Use the <IC>python-dotenv</IC> package to load them:
              </Typography>
              <CodeBlock label="Python — load .env" code={`# Install the package\npip install python-dotenv

      # At the top of your script
      from dotenv import load_dotenv
      load_dotenv()  # This loads variables from .env into os.environ

      # Now os.environ["OPENAI_API_KEY"] and os.environ["OPENAI_BASE_URL"] are available
      `} />
            </InfoBox>

          {/* LangChain */}
          <SectionTitle id="fw-langchain" sub>LangChain</SectionTitle>
          {para('LangChain\'s ChatOpenAI and OpenAIEmbeddings both honour OPENAI_API_KEY and OPENAI_BASE_URL automatically.')}
          <CodeBlock label="Python — LangChain" code={`
# pip install langchain-openai
from langchain_openai import ChatOpenAI, OpenAIEmbeddings

# No extra config — picks up env vars automatically
llm = ChatOpenAI(model="gpt-4o", temperature=0.7)
response = llm.invoke("Explain RAG in one sentence.")
print(response.content)

# Embeddings work the same way
embeddings = OpenAIEmbeddings(model="text-embedding-3-small")
vector = embeddings.embed_query("Hello world")`} />
          <CodeBlock label="Python — LangChain (explicit config)" code={`
import os
from langchain_openai import ChatOpenAI

llm = ChatOpenAI(
    model="gpt-4o",
    openai_api_key=os.environ["OPENAI_API_KEY"],
    openai_api_base=os.environ["OPENAI_BASE_URL"],  # https://agenticqe.ai/v1
)`} />

          {/* LlamaIndex */}
          <SectionTitle id="fw-llamaindex" sub>LlamaIndex</SectionTitle>
          {para('LlamaIndex\'s OpenAI model class reads the standard env vars out of the box.')}
          <CodeBlock label="Python — LlamaIndex" code={`
# pip install llama-index-llms-openai llama-index-embeddings-openai
from llama_index.llms.openai import OpenAI
from llama_index.embeddings.openai import OpenAIEmbedding

# Auto-reads OPENAI_API_KEY and OPENAI_BASE_URL from env
llm = OpenAI(model="gpt-4o")
response = llm.complete("Explain vector databases in one sentence.")
print(response.text)

embeddings = OpenAIEmbedding(model="text-embedding-3-small")

# — or with explicit config:
# llm = OpenAI(
#     model="gpt-4o",
#     api_key=os.environ["OPENAI_API_KEY"],
#     api_base=os.environ["OPENAI_BASE_URL"],  # https://agenticqe.ai/v1
# )`} />

          {/* CrewAI */}
          <SectionTitle id="fw-crewai" sub>CrewAI</SectionTitle>
          {para('CrewAI uses LangChain under the hood. Set the env vars and all agents in your crew route through the wrapper.')}
          <CodeBlock label="Python — CrewAI" code={`
# pip install crewai langchain-openai
import os
from crewai import Agent, Task, Crew, LLM
from dotenv import load_dotenv
load_dotenv()
# Wrapper-aware LLM (env vars already set)
llm = LLM(model="openai/gpt-4o")

researcher = Agent(
    role="Senior Researcher",
    goal="Research the given topic thoroughly",
    backstory="You are an expert analyst with years of experience.",
    llm=llm,
    verbose=True,
)

task = Task(
    description="Research the impact of LLMs on software development.",
    agent=researcher,
    expected_output="A concise 3-paragraph summary.",
)

crew = Crew(agents=[researcher], tasks=[task])
result = crew.kickoff()
print(result)`} />

          {/* AutoGen */}
          <SectionTitle id="fw-autogen" sub>AutoGen / AG2</SectionTitle>
          {para('AutoGen supports custom OpenAI-compatible endpoints via its config_list. Pass your auth key and the wrapper base URL directly.')}
          <CodeBlock label="Python — AutoGen" code={`
# pip install pyautogen
import os
import autogen

config_list = [
    {
        "model":    "gpt-4o",
        "api_key":  os.environ["OPENAI_API_KEY"],
        "base_url": os.environ["OPENAI_BASE_URL"],  # https://agenticqe.ai/v1
    }
]

llm_config = {"config_list": config_list, "temperature": 0}

assistant = autogen.AssistantAgent(
    name="assistant",
    llm_config=llm_config,
)

user_proxy = autogen.UserProxyAgent(
    name="user_proxy",
    human_input_mode="NEVER",
    max_consecutive_auto_reply=3,
    code_execution_config={"work_dir": "coding"},
)

user_proxy.initiate_chat(
    assistant,
    message="Write a Python function to check if a number is prime.",
)`} />

          {divider()}

          {/* ── Best Practices ───────────────────────────────────── */}
          <SectionTitle id="best-practices">Best Practices</SectionTitle>
          {[
            ['Use standard OpenAI env vars',     'Store your auth key in OPENAI_API_KEY and the base URL in OPENAI_BASE_URL. Every OpenAI-compatible SDK and framework picks these up with zero code changes.'],
            ['Implement retry with backoff',     'Retry on 429, 500, 502, 503, 504 with exponential backoff. The [ProxyWrapper] prefix in error messages makes it easy to distinguish quota errors (wait for reset) from transient errors (retry immediately).'],
            ['Read the error prefix',            'Check the prefix before deciding how to handle an error. [ProxyWrapper] errors are your quota or permission issue; [Error from OpenAI] errors are upstream model issues.'],
            ['Cache the models list',            'Available models rarely change. Cache the /v1/models response for at least one hour to avoid repeated overhead.'],
            ['Set request timeouts',             'Use 30 s for non-streaming and 60 s for streaming requests. Always set a timeout — long requests without one can hang your application indefinitely.'],
            ['Track token usage',               'Log prompt_tokens and completion_tokens from each response for your own cost monitoring and capacity planning ahead of quota resets.'],
            ['Graceful degradation on 429',     'When a [ProxyWrapper] 429 occurs, fall back to a cheaper model or queue the request until midnight UTC when quotas reset.'],
          ].map(([title, desc]) => (
            <Box key={title} sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FF1A50', flexShrink: 0, mt: '10px' }} />
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: textP, mb: 0.4 }}>{title}</Typography>
                <Typography sx={{ fontSize: '0.83rem', color: textS, lineHeight: 1.7 }}>{desc}</Typography>
              </Box>
            </Box>
          ))}

          {/* Footer */}
          <Box sx={{ mt: 8, pt: 4, borderTop: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.78rem', color: textM }}>LLM Wrapper — API Docs</Typography>
            <Box
              onClick={() => navigate('/login')}
              sx={{ fontSize: '0.78rem', color: textM, cursor: 'pointer', '&:hover': { color: '#FF1A50' }, transition: 'color 0.15s' }}
            >
              Sign in to the console →
            </Box>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default DocsPage;
